/**
 * @module public/js/core/tenantRoles
 * @summary Shared helpers for tenant-scoped role normalization in the browser.
 */

import ROLES from '../constants/roles.js';

/**
 * Ordered tenant role list used for normalization and legacy-role derivation.
 *
 * `super_admin` is intentionally excluded because it is a global custom claim,
 * not a tenant-scoped Firestore role.
 *
 * @type {readonly string[]}
 */
export const TENANT_ROLE_ORDER = Object.freeze([
  ROLES.UNASSIGNED,
  ROLES.SELLER,
  ROLES.MANAGER,
  ROLES.ADMIN,
]);

/**
 * Normalize Firestore or form role values into a stable tenant-role array.
 *
 * Accepts the app's legacy role shapes: a single string, an array, or an
 * object-set such as `{ admin: true }`. Unknown values are discarded.
 *
 * @param {string|string[]|Record<string, boolean>|null|undefined} rawRoles - Role source to normalize.
 * @param {string|null} [legacyRole=null] - Legacy single-role fallback.
 * @returns {string[]} Ordered list of valid tenant roles.
 */
export function normalizeTenantRoles(rawRoles, legacyRole = null) {
  const collected = new Set();

  const addRole = (value) => {
    if (typeof value !== 'string') return;
    const normalized = value.trim().toLowerCase();
    if (TENANT_ROLE_ORDER.includes(normalized)) {
      collected.add(normalized);
    }
  };

  if (Array.isArray(rawRoles)) {
    rawRoles.forEach(addRole);
  } else if (rawRoles && typeof rawRoles === 'object') {
    Object.entries(rawRoles).forEach(([role, enabled]) => {
      if (enabled) addRole(role);
    });
  } else {
    addRole(rawRoles);
  }

  if (collected.size === 0) {
    addRole(legacyRole);
  }

  if (collected.has(ROLES.UNASSIGNED) && collected.size > 1) {
    collected.delete(ROLES.UNASSIGNED);
  }

  return TENANT_ROLE_ORDER.filter((role) => collected.has(role));
}

/**
 * Derive the legacy single-role value from normalized tenant roles.
 *
 * The highest tenant privilege wins so compatibility reads of `role` continue
 * to observe the broadest tenant access represented in `roles`.
 *
 * @param {string[]} roles - Normalized tenant role list.
 * @returns {string} Highest tenant role, defaulting to `seller`.
 */
export function derivePrimaryTenantRole(roles) {
  const normalizedRoles = normalizeTenantRoles(roles, null);
  return normalizedRoles[normalizedRoles.length - 1] || ROLES.SELLER;
}

/**
 * Build reporting-specific role state from a tenant user document.
 *
 * Reporting eligibility is seller-only and excludes disabled users. During the
 * role migration window, the legacy single `role` field is used only when the
 * canonical `roles` field is absent or empty.
 *
 * @param {Record<string, any>|null|undefined} data - Tenant user document data.
 * @returns {{ role: string, roles: string[], disabled: boolean, isSellerEligible: boolean }} Normalized reporting state.
 */
export function getTenantUserReportingState(data) {
  const safeData = data && typeof data === 'object' ? data : {};
  const roles = normalizeTenantRoles(safeData.roles, safeData.role);
  const role = derivePrimaryTenantRole(roles);
  const disabled = safeData.disabled === true;

  return {
    role,
    roles,
    disabled,
    isSellerEligible: roles.includes(ROLES.SELLER) && !disabled,
  };
}
